import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, BarChart3 } from "lucide-react";
import toast from "react-hot-toast";

import { getProposalReport } from "../api/prospectingReportsApi";

const TEAL = "#0f8d9b";

const Tile = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
    <div className="text-2xl font-extrabold tabular-nums" style={{ color: TEAL }}>{value}</div>
    <div className="text-xs font-semibold text-gray-500 mt-0.5">{label}</div>
    {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

/**
 * Proposal-to-listing reporting: engagement and conversion metrics, plus a
 * per-agent breakdown for admins/PAs. The data CG framed as the point of the
 * product ("the infrastructure").
 */
const ProposalsReportPage = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProposalReport()
      .then(({ data }) => setReport(data))
      .catch(() => toast.error("Could not load the report."))
      .finally(() => setLoading(false));
  }, []);

  const s = report?.summary;

  return (
    <div className="p-4 md:p-6">
      <button onClick={() => navigate("/prospecting/proposals")} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4">
        <ArrowLeft className="w-4 h-4" /> Proposals
      </button>
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-1">
        <BarChart3 className="w-6 h-6" style={{ color: TEAL }} /> Proposal report
      </h1>
      <p className="text-sm text-gray-500 mb-5">Engagement and appraisal-to-listing conversion.</p>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : !s ? (
        <div className="text-sm text-gray-500">No data yet.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
            <Tile label="Sent" value={s.sent} sub={`${s.drafts} draft${s.drafts === 1 ? "" : "s"}`} />
            <Tile label="Viewed" value={s.viewed} sub={`${s.view_rate}% view rate`} />
            <Tile label="Accepted" value={s.accepted} sub={`${s.acceptance_rate}% of sent`} />
            <Tile label="Appraisal→listing" value={`${s.conversion_rate}%`} sub={`${s.won} won · ${s.lost} lost`} />
            <Tile label="Callback requests" value={s.callback_requests} />
            <Tile label="Total views" value={s.total_views} />
          </div>

          {Array.isArray(report.by_agent) && report.by_agent.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 text-sm font-bold text-gray-900">By agent</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 uppercase text-[11px]">
                      <th className="px-4 py-2">Agent</th>
                      <th className="px-4 py-2 text-right">Sent</th>
                      <th className="px-4 py-2 text-right">Viewed</th>
                      <th className="px-4 py-2 text-right">Accepted</th>
                      <th className="px-4 py-2 text-right">Won</th>
                      <th className="px-4 py-2 text-right">Accept %</th>
                      <th className="px-4 py-2 text-right">Conv %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_agent.map((r) => (
                      <tr key={r.agent_id} className="border-t border-gray-100">
                        <td className="px-4 py-2 font-medium text-gray-800">{r.agent_name || "—"}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.sent}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.viewed}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.accepted}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-semibold" style={{ color: TEAL }}>{r.won}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.acceptance_rate}%</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.conversion_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProposalsReportPage;
